import React, { FC, useState, useEffect } from 'react'
import { fetchAllClientData } from 'apis/client'
import { updateMyUserPreferences } from 'apis/private/userPreferences'
import { validateBrowserStoreLocale, getBrowserCurrentCountryCode } from 'lib/storeLocale/client'
import UserPreferences, { ConnectorProps } from 'connectors/UserPreferences'
import ModalConnector, { ConnectorProps as ModalProps } from 'connectors/Modal'
import { setClientStorage, setClientStorageCookie } from 'store/middleware/client'
import Modal from 'components/SA/Modal/CustomAlert'
import styles from './StoreLocale.module.scss'
import { setUserPreferencesCookie } from 'apis/cookies'

type PreferenceStoreLocaleAlertProps = ModalProps & ConnectorProps

export const MODAL_NAME = 'user-preferences-alert'

const PreferenceStoreLocaleAlert: FC<PreferenceStoreLocaleAlertProps> = (props) => {
  const {
    availableCountries,
    closeModal,
    isAuthenticated,
    updateClientData,
    userPreferences,
    userPreferencesConfirmed,
    userPreferencesConfirmedCountryCode,
    userPreferencesConfirmedCountryName,
  } = props

  const [hasConfirmed, handleConfirmation] = useState<boolean>(false)
  const [browserCountryName, handleBrowserCountryName] = useState<string>('')

  useEffect(() => {
    const countryCode = getBrowserCurrentCountryCode()
    const countryName = availableCountries ? availableCountries[countryCode] : countryCode
    handleBrowserCountryName(countryName)
  }, [])

  const confirmCurrentStoreLocale = async () => {
    const cookieData = {
      ...userPreferences,
      countryCode: getBrowserCurrentCountryCode(),
    }
    if (isAuthenticated) {
      // If authenticated write USER preferences - Gallery Endpoint
      await updateMyUserPreferences(cookieData)
    }
    await setUserPreferencesCookie(cookieData)

    const { success, payload: allClientData } = await fetchAllClientData()
    if (!success) {
      console.error('Problem Fetching Session')
      closeModal()
      return
    }
    setClientStorage(allClientData)
    updateClientData(allClientData) // update user preferences in the DB from session
    handleConfirmation(true)
    closeModal()
  }

  const customCloseAction = async (ev) => {
    const { payload } = await setUserPreferencesCookie({
      countryCode: userPreferencesConfirmedCountryCode,
      neverConfirmed: !userPreferencesConfirmed, // Guest or First Time user get served the wrong Currency
    })
    setClientStorageCookie(payload)
    const { storeLocale } = payload
    const redirectRequired = validateBrowserStoreLocale(storeLocale)
    if (redirectRequired) {
      return
    }
    handleConfirmation(true)
    closeModal()
  }

  // Lets handle ISSUE with `United States to United States`
  const hasBadMessaging = browserCountryName === userPreferencesConfirmedCountryName

  // We need to Handle Multiple Renders OR Continuos Prompts
  // Once user confirms don't RENDER until new page load
  if (hasConfirmed || hasBadMessaging) {
    return null
  }

  return (
    <Modal
      name={MODAL_NAME}
      onCloseModal={confirmCurrentStoreLocale}
      isCloseButtonVisible
      styleOverride={styles.alert}
    >
      {/* CSS Target <aside> */}
      <>
        <p>
          It seems that you are browsing from {userPreferencesConfirmedCountryName}. Change your
          country preference from {browserCountryName} to {userPreferencesConfirmedCountryName}?
        </p>

        <div className={styles.buttons}>
          <button type='button' data-action='cancel' onClick={confirmCurrentStoreLocale}>
            No
          </button>

          <button type='button' data-action='confirm' onClick={customCloseAction}>
            yes
          </button>
        </div>
      </>
    </Modal>
  )
}

export default ModalConnector(UserPreferences(PreferenceStoreLocaleAlert))
